import * as Sentry from '@sentry/browser';
import { ApiError } from '~/errors/api-error';
import { httpErrorDetailsSchema } from '~/schemas/errors';
export const httpMethods = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE', 'OPTIONS'] as const;
export const supportedContentTypes = ['application/json', 'application/pdf', 'application/zip', 'text/csv'] as const;
export const FILE_SIZE_LIMIT = 200 * 1024 * 1024; // 200MB
// The production WAF (`max_request_body_size_in_kb = 2000`) rejects larger bodies with a 403 before they reach the API
export const REQUEST_BODY_SIZE_LIMIT = 2000 * 1024; // 2000KB
export const DEFAULT_PAGE_SIZE = 30_000;
/** How many matches a server search brings back; more than fits a list, and the search narrows it. */
export const SEARCH_LIMIT = 100;

export type HttpMethods = (typeof httpMethods)[number];
export type SupportedContentTypes = (typeof supportedContentTypes)[number];

/* -------------------------------------------------------------------------------------------------
 * Helpers
 * -----------------------------------------------------------------------------------------------*/

function toUrl(path: string, queryParams?: URLSearchParams) {
  const prefix = path.startsWith('https://');
  const url = prefix ? new URL(path) : new URL(`${API_PATH}${path}`, BACKEND_HOST);
  if (queryParams) {
    url.search = queryParams.toString();
  }
  return url.toString();
}

function toFormData<T>(data: T) {
  if (data instanceof Object) {
    return Object.entries(data).reduce((formData, [key, value]) => {
      formData.append(key, value);
      return formData;
    }, new FormData());
  }
}

function toRequestBody<T>(data?: T, isMultiPart?: boolean) {
  if (!data) {
    return undefined;
  }

  return isMultiPart ? toFormData(data) : JSON.stringify(data);
}

function toValidationErrors(detail: unknown): Array<string> | undefined {
  const details = httpErrorDetailsSchema.safeParse(detail);

  if (!details.success) {
    return undefined;
  }

  return details.data.map(({ loc, msg, type }) => `${loc.join('.')}: ${msg} (${type})`);
}

/* -------------------------------------------------------------------------------------------------
 * Fetch
 * -----------------------------------------------------------------------------------------------*/
interface RequestOptions extends Omit<RequestInit, 'method' | 'body'> {
  headers?: HeadersInit;
}

export interface FetchConfig<RequestBody> {
  method?: HttpMethods;
  queryParams?: URLSearchParams;
  body?: RequestBody;
  options?: RequestOptions;
  isMultiPart?: boolean;
  /** Receives the headers of a successful response, for callers that need more than the body. */
  onResponseHeaders?: (headers: Headers) => void;
}

export async function fetchApi<RequestBody, ResponseType>(
  endpoint: string,
  config: FetchConfig<RequestBody> = { method: 'GET' },
) {
  const { body, method, queryParams, isMultiPart, options, onResponseHeaders } = config;

  const url = toUrl(endpoint, queryParams);
  const { file } = (body || {}) as Record<string, File>;

  if (isMultiPart && file instanceof File && file?.size >= FILE_SIZE_LIMIT) {
    throw new ApiError('FILE_EXCEEDS_SIZE_LIMIT', 413);
  }

  const response = await fetch(url, {
    ...options,
    body: toRequestBody(body, isMultiPart),
    headers: {
      ...(!isMultiPart ? { 'Content-Type': 'application/json' } : {}),
      ...options?.headers,
    },
    method: method,
  });

  if (!response.ok) {
    const requestId = response.headers.get('X-Request-Id') ?? undefined;

    const errorResponse = await response.json().catch(() => null);

    const errorDetail = errorResponse?.detail ?? errorResponse;
    const errorMessage =
      typeof errorDetail === 'string'
        ? errorDetail
        : Array.isArray(errorDetail) && errorDetail[0]?.msg
          ? errorDetail[0].msg
          : '';

    const exception = new ApiError(errorMessage, response.status, errorDetail, requestId);

    Sentry.captureException(exception, {
      contexts: {
        api: {
          endpoint,
          method: method ?? 'GET',
          request_id: requestId,
          status: response.status,
          validation_errors: toValidationErrors(errorDetail),
        },
      },
    });

    throw exception;
  }

  onResponseHeaders?.(response.headers);

  // 204 No Content has an empty body even when a Content-Type is present, so calling response.json()
  // would throw "Unexpected end of JSON input". Return early for any successful empty response.
  if (response.status === 204) {
    return undefined as ResponseType;
  }

  const contentType = response.headers.get('Content-Type')?.split(';')?.[0] as SupportedContentTypes | null;

  if (!contentType) {
    return undefined as ResponseType;
  }

  if (contentType === 'application/json') {
    try {
      return (await response.json()) as ResponseType;
    } catch (error) {
      // Report a successful response whose body fails to decode.
      Sentry.captureException(error, {
        extra: { responseContentLength: response.headers.get('Content-Length'), responseURL: url },
        tags: { responseBodyDecode: 'failed' },
      });
      throw error;
    }
  }

  if (contentType === 'text/csv' || contentType === 'application/pdf' || contentType === 'application/zip') {
    return response.blob() as Promise<ResponseType>;
  }

  throw new Error(`Unsupported Content-Type ${contentType}`);
}
